import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Container, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SecureEmail from '../components/SecureEmail';
import { StyledButton } from '../components/ui';
import { LegalPageLayout } from '../layouts';

/**
 * 500 Server Error page component
 *
 * @returns {JSX.Element} 500 error page
 */
function ServerError() {
  const navigate = useNavigate();

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <LegalPageLayout>
      <Box
        sx={{
          minHeight: 'calc(100vh - 84px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--color-background-light)',
          backgroundImage: 'linear-gradient(135deg, #ffebee 0%, #fce4ec 100%)',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            {/* Error Icon */}
            <ErrorOutlineIcon
              sx={{
                fontSize: { xs: '4rem', sm: '5rem', md: '6rem' },
                color: '#d32f2f',
                mb: 2,
              }}
            />

            {/* 500 Number */}
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '4rem', sm: '5rem', md: '6rem' },
                fontWeight: 900,
                color: '#d32f2f',
                lineHeight: 0.9,
                mb: 2,
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                textShadow: '0 4px 8px rgba(0,0,0,0.1)',
              }}
            >
              500
            </Typography>

            {/* Error Message */}
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                fontWeight: 600,
                color: 'var(--color-heading)',
                mb: 2,
                fontFamily: '"Playfair Display", serif',
              }}
            >
              Something Went Wrong
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: '1.125rem',
                color: 'var(--color-text-muted)',
                mb: 4,
                maxWidth: '500px',
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              We're experiencing some technical difficulties. Our team has been
              notified and is working to resolve the issue. Please try again in
              a few moments.
            </Typography>

            {/* Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                flexWrap: 'wrap',
                mb: 4,
              }}
            >
              <StyledButton
                variant="contained"
                size="large"
                onClick={handleRefresh}
                sx={{
                  minWidth: '160px',
                  backgroundColor: '#d32f2f',
                  '&:hover': {
                    backgroundColor: '#c62828',
                  },
                }}
              >
                Try Again
              </StyledButton>

              <StyledButton
                variant="outlined"
                size="large"
                onClick={() => navigate('/')}
                sx={{
                  minWidth: '160px',
                  borderColor: 'var(--color-primary)',
                  color: 'var(--color-primary)',
                  '&:hover': {
                    backgroundColor:
                      'color-mix(in srgb, var(--color-primary) 10%, transparent)',
                    borderColor: 'var(--color-primary)',
                  },
                }}
              >
                Go Home
              </StyledButton>
            </Box>

            {/* Support Information */}
            <Box sx={{ pt: 4, borderTop: '1px solid var(--color-border)' }}>
              <Typography
                variant="body2"
                sx={{ color: 'var(--color-text-muted)', mb: 1 }}
              >
                If this problem persists, please contact our support team
              </Typography>

              <SecureEmail
                emailKey="support"
                sx={{
                  color: 'var(--color-primary)',
                  textDecoration: 'underline',
                  fontSize: '1rem',
                  '&:hover': { color: 'var(--color-primary-dark)' },
                }}
                fallbackText="Support email (JavaScript required)"
              />
            </Box>

            {/* Error Code for Support */}
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 3,
                color: 'var(--color-text-muted)',
                fontSize: '0.875rem',
              }}
            >
              Error Code: SRV-500-{Date.now().toString().slice(-6)}
            </Typography>
          </Box>
        </Container>
      </Box>
    </LegalPageLayout>
  );
}

export default ServerError;
